import { Injectable } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { GetParticipantIdFromRouteService } from "@modules/participant-shared/services/get-participant-id-from-route.service";
import { filter, map, Observable, Subject, tap } from "rxjs";

/**
 * Service to track the most recent participant id based on the route.
 */
@Injectable({
    providedIn: 'root',
})
export class MostRecentParticipantIdService {
    /**
     * Subject to emit participant id changes.
     */
    private readonly _participantIdChangedSubject: Subject<number> = new Subject<number>();
    
    /**
     * The most recent participant id based on the route, or undefined if the route
     * has never had a participant id in it. Only changes if the new route has a
     * participant id in it.
     */
    private _mostRecentParticipantId: number | undefined;
    
    constructor(
        private readonly _getParticipantIdFromRouteService: GetParticipantIdFromRouteService,
        router: Router,
    ) {
        this._mostRecentParticipantId = this._getParticipantIdFromRouteService.getParticipantIdFromRoute();

        //Subscribe to route changes that have a participant id, and emit the participant id
        //whenever it is different from the most recent participant id.
        router
            .events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                map(() => this._getParticipantIdFromRouteService.getParticipantIdFromRoute()),
                filter(currentParticipantId => !!currentParticipantId && currentParticipantId != this._mostRecentParticipantId),
                map(currentParticipantId => currentParticipantId!),
                tap(currentParticipantId => this._mostRecentParticipantId = currentParticipantId)
            )
            .subscribe(this._participantIdChangedSubject);
    }

    /**
     * Returns the most recent participant id based on the route, or undefined if the
     * route has never had a participant id in it. Only changes if the new route has
     * a participant id in it.
     */
    public getMostRecentParticipantId(): number | undefined {
        return this._mostRecentParticipantId;
    }

    /**
     * Returns an observable that emits the most recent participant id based on the route
     * whenever it changes. Only emits if the new route has a participant id in it.
     */
    public participantIdChanged(): Observable<number> {
        return this._participantIdChangedSubject.asObservable();
    }
}